import { MINIGAME_IDS } from '@minigame-race/contracts';
import { Screen } from '../components/Chrome.js';
import { Icon } from '../components/Icon.js';
import { MINIGAME_META } from '../race/registry.js';
import { color } from '../theme.js';

/**
 * How the game works, as its own page.
 *
 * Layout note: `Screen` is a fixed-height, `overflow:hidden` container. So the
 * scrolling lives on the content column, not the page — the header stays put and
 * nothing can push the body sideways. Everything sizes from `min(...)` and
 * `clamp(...)`, so this works from a 320px phone up to a desktop without any
 * media queries.
 *
 * The per-game content is read from MINIGAME_META, which is also what the
 * in-race "?" dialog reads. Register a minigame and it documents itself in both
 * places — there is no list here to forget to update.
 */
export function RulesScreen({ onBack }: { onBack: () => void }) {
  return (
    <Screen brackets="top">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 'clamp(16px,3.4cqmin,38px)',
          gap: 'clamp(12px,2cqmin,20px)',
          minHeight: 0,
        }}
      >
        {/* header — fixed, never scrolls away */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              border: '1px solid rgba(166,255,46,.25)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.05)',
              color: color.limeBright,
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '.04em',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <Icon name="chevron-left" size={15} /> BACK
          </button>
          <span
            style={{
              fontWeight: 700,
              fontSize: 'clamp(15px,3cqmin,22px)',
              letterSpacing: '.06em',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            HOW IT WORKS
          </span>
        </div>

        {/* the one scroll container */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 'min(100%,640px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(14px,2.4cqmin,24px)',
              paddingBottom: 24,
            }}
          >
            <Section title="THE RACE">
              <P>
                You and your opponent play the <B>same minigames at the same time</B>, in the same
                order, with the same puzzles.
              </P>
              <P>
                Whoever solves a minigame first, wins the moment — but the other player still gets a
                few seconds to land it too. Then you <B>both move on together</B>.
              </P>
              <P>
                Some games give no extra time at all. Tile Tap is a pure speed race: first to finish
                ends it.
              </P>
            </Section>

            <Section title="SCORING">
              <P>
                Everything is measured in <B>seconds</B>, and the lowest total wins.
              </P>
              <ul style={ulStyle}>
                <Li>Your time on each minigame is added up.</Li>
                <Li>Mistakes and hints add seconds.</Li>
                <Li>Failing to solve a minigame adds a lot of seconds.</Li>
              </ul>
              <P dim>
                So finishing fast is good, but finishing at all matters more. A hint that saves you
                from failing has already paid for itself.
              </P>
            </Section>

            <Section title="THE CLOCK">
              <P>
                Each minigame has its own time limit. Run out and that game closes — you take the
                penalty, and you move on.
              </P>
              <P dim>
                A bad round can&apos;t eat the time budgeted for the next game. The clock never
                pauses, not even if you disconnect.
              </P>
            </Section>

            <Section title="THE MINIGAMES">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {MINIGAME_IDS.map((id) => {
                  const meta = MINIGAME_META[id];
                  return (
                    <div
                      key={id}
                      style={{
                        border: '1px solid rgba(166,255,46,.14)',
                        borderRadius: 12,
                        background: 'rgba(166,255,46,.035)',
                        padding: 'clamp(12px,2cqmin,18px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '.05em', color: color.limeBright }}>
                          {meta.title}
                        </span>
                        <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'rgba(234,255,234,.35)' }}>
                          {meta.subtitle}
                        </span>
                      </div>
                      <P>{meta.goal}</P>
                      <ul style={ulStyle}>
                        {meta.rules.map((rule) => (
                          <Li key={rule}>{rule}</Li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="RANK">
              <P>
                Ranked matches move your ELO. Games against a friend in a private room never do.
              </P>
            </Section>
          </div>
        </div>
      </div>
    </Screen>
  );
}

const ulStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 11, letterSpacing: '.22em', color: 'rgba(166,255,46,.6)', fontWeight: 600, flexShrink: 0 }}>
          {title}
        </span>
        <div style={{ flex: 1, height: 1, background: 'rgba(166,255,46,.12)' }} />
      </div>
      {children}
    </section>
  );
}

function P({ children, dim = false }: { children: React.ReactNode; dim?: boolean }) {
  return (
    <p
      style={{
        margin: 0,
        fontSize: 'clamp(12.5px,1.9cqmin,14.5px)',
        lineHeight: 1.55,
        color: dim ? 'rgba(234,255,234,.5)' : 'rgba(234,255,234,.78)',
      }}
    >
      {children}
    </p>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ fontSize: 'clamp(12px,1.8cqmin,13.5px)', lineHeight: 1.5, color: 'rgba(234,255,234,.68)' }}>
      {children}
    </li>
  );
}

function B({ children }: { children: React.ReactNode }) {
  return <strong style={{ color: color.text, fontWeight: 700 }}>{children}</strong>;
}
